// @ts-nocheck
/**
 * ファンド指数計算エンジン
 *
 * カード価格から各ファンドの指数を計算する
 * ファンドは独自の価格を持たず、常にカード価格から導出される
 *
 * 設計: market_economy_system_v3.md
 */

import { calculateBaseValue } from '../systems/valueCalculator';
import { getCardMarketPrice } from './marketEngine';
import { ALL_FUNDS, FUND_TYPES } from '../investment/data/funds';
import {
  getCardsByAttribute,
  getCardsByCategory,
  getCardsByCategories,
  getLowCostCards,
  getHighCostCards,
} from './marketIndex';

// ========================================
// キャッシュ管理
// ========================================

let cache = {};
let cacheDay = -1;

/**
 * キャッシュをクリア（日付が変わった時に呼び出し）
 *
 * @param {number} currentDay - 現在の日付
 */
export function clearCacheIfNeeded(currentDay) {
  if (currentDay !== cacheDay) {
    cache = {};
    cacheDay = currentDay;
  }
}

/**
 * キャッシュを強制クリア
 */
export function clearCache() {
  cache = {};
  cacheDay = -1;
}

// ========================================
// 指数計算
// ========================================

/**
 * カードリストの指数を計算
 * 指数 = (現在価格合計 / 基礎価格合計) × 100
 *
 * @param {Object[]} cards - カードリスト
 * @param {Object} marketState - 市場状態
 * @returns {number} 指数（100 = 基準値）
 */
export function calculateCardsIndex(cards, marketState) {
  if (!cards || cards.length === 0) {
    return 100; // デフォルト値
  }

  let totalCurrentPrice = 0;
  let totalBasePrice = 0;

  for (const card of cards) {
    const baseValue = calculateBaseValue(card);
    const { price } = getCardMarketPrice(card, baseValue, marketState);

    totalCurrentPrice += price;
    totalBasePrice += baseValue;
  }

  if (totalBasePrice === 0) {
    return 100;
  }

  // 指数化（100 = 基準値）
  return (totalCurrentPrice / totalBasePrice) * 100;
}

/**
 * 属性の指数を計算
 *
 * @param {string} attribute - 属性
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @returns {number} 指数
 */
export function getAttributeIndex(attribute, marketIndex, marketState) {
  const cacheKey = `attr_${attribute}`;
  if (cache[cacheKey] !== undefined) {
    return cache[cacheKey];
  }

  const cards = getCardsByAttribute(marketIndex, attribute);
  const index = calculateCardsIndex(cards, marketState);

  cache[cacheKey] = index;
  return index;
}

/**
 * カテゴリの指数を計算
 *
 * @param {string} category - カテゴリ
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @returns {number} 指数
 */
export function getCategoryIndex(category, marketIndex, marketState) {
  const normalizedCategory = category.replace(/【|】/g, '').trim();
  const cacheKey = `cat_${normalizedCategory}`;
  if (cache[cacheKey] !== undefined) {
    return cache[cacheKey];
  }

  const cards = getCardsByCategory(marketIndex, normalizedCategory);
  const index = calculateCardsIndex(cards, marketState);

  cache[cacheKey] = index;
  return index;
}

/**
 * 複数カテゴリの指数を計算
 *
 * @param {string[]} categories - カテゴリ配列
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @returns {number} 指数
 */
export function getCategoriesIndex(categories, marketIndex, marketState) {
  if (!categories || categories.length === 0) {
    return 100;
  }

  // キャッシュキーを生成（カテゴリをソートして結合）
  const sortedCategories = [...categories].sort().join(',');
  const cacheKey = `cats_${sortedCategories}`;
  if (cache[cacheKey] !== undefined) {
    return cache[cacheKey];
  }

  const cards = getCardsByCategories(marketIndex, categories);
  const index = calculateCardsIndex(cards, marketState);

  cache[cacheKey] = index;
  return index;
}

/**
 * 低コストカードの指数を計算（cost <= 2）
 *
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @returns {number} 指数
 */
export function getLowCostIndex(marketIndex, marketState) {
  const cacheKey = 'cost_low';
  if (cache[cacheKey] !== undefined) {
    return cache[cacheKey];
  }

  const cards = getLowCostCards(marketIndex);
  const index = calculateCardsIndex(cards, marketState);

  cache[cacheKey] = index;
  return index;
}

/**
 * 高コストカードの指数を計算（cost >= 5）
 *
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @returns {number} 指数
 */
export function getHighCostIndex(marketIndex, marketState) {
  const cacheKey = 'cost_high';
  if (cache[cacheKey] !== undefined) {
    return cache[cacheKey];
  }

  const cards = getHighCostCards(marketIndex);
  const index = calculateCardsIndex(cards, marketState);

  cache[cacheKey] = index;
  return index;
}

/**
 * 市場全体の指数（MSI: Magic Spirit Index）を計算
 *
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @returns {number} 指数
 */
export function getMarketIndex(marketIndex, marketState) {
  const cacheKey = 'msi';
  if (cache[cacheKey] !== undefined) {
    return cache[cacheKey];
  }

  const cards = marketIndex.allCards;
  const index = calculateCardsIndex(cards, marketState);

  cache[cacheKey] = index;
  return index;
}

// ========================================
// ファンド指数計算
// ========================================

/**
 * ファンドIDから指数を計算
 *
 * サーバーで計算済みの指数がある場合はそれを優先使用。
 * サーバー指数がない場合のみクライアント側で計算（フォールバック）。
 *
 * @param {string} fundId - ファンドID
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @returns {number} 指数（100 = 基準値）
 */
export function getFundIndex(fundId, marketIndex, marketState) {
  const cacheKey = `fund_${fundId}`;
  if (cache[cacheKey] !== undefined) {
    return cache[cacheKey];
  }

  const fund = ALL_FUNDS[fundId];
  if (!fund) {
    return 100;
  }

  let index;

  // サーバーで計算済みの指数があれば優先使用
  // serverIndexes には "fund_region_fire" のようなキーで格納されている
  if (marketState?.serverIndexes) {
    const serverKey = `fund_${fundId}`;
    if (marketState.serverIndexes[serverKey] !== undefined) {
      index = marketState.serverIndexes[serverKey];
      cache[cacheKey] = index;
      return index;
    }
  }

  // フォールバック: クライアント側で計算
  switch (fund.type) {
    case FUND_TYPES.REGION:
      // 地域ファンド: 属性ベース
      index = getAttributeIndex(fund.attribute, marketIndex, marketState);
      break;

    case FUND_TYPES.META:
      // メタファンド: スタイルベース
      if (fund.style === 'aggro') {
        index = getLowCostIndex(marketIndex, marketState);
      } else if (fund.style === 'control') {
        index = getHighCostIndex(marketIndex, marketState);
      } else {
        index = 100;
      }
      break;

    case FUND_TYPES.CATEGORY:
      // カテゴリファンド: カテゴリベース（複数カテゴリ対応）
      index = getCategoriesIndex(fund.categories, marketIndex, marketState);
      break;

    default:
      index = 100;
  }

  cache[cacheKey] = index;
  return index;
}

/**
 * ファンドの価格を計算（指数 × 基準価格 / 100）
 *
 * @param {string} fundId - ファンドID
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @returns {number} 価格
 */
export function getFundPrice(fundId, marketIndex, marketState) {
  const fund = ALL_FUNDS[fundId];
  if (!fund) {
    return 10000;
  }

  const index = getFundIndex(fundId, marketIndex, marketState);
  const basePrice = fund.basePrice || 10000;

  return Math.round((index / 100) * basePrice);
}

/**
 * ファンドの変動率を計算（%）
 *
 * @param {string} fundId - ファンドID
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @returns {number} 変動率（例: 20 = +20%）
 */
export function getFundChangeRate(fundId, marketIndex, marketState) {
  const index = getFundIndex(fundId, marketIndex, marketState);
  return index - 100; // 100が基準なので、それとの差分
}

/**
 * 全ファンドの指数を取得
 *
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @returns {Object} { fundId: { index, price, changeRate } }
 */
export function getAllFundIndices(marketIndex, marketState) {
  const result = {};

  for (const fundId of Object.keys(ALL_FUNDS)) {
    const index = getFundIndex(fundId, marketIndex, marketState);
    const price = getFundPrice(fundId, marketIndex, marketState);
    const changeRate = index - 100;

    result[fundId] = {
      index,
      price,
      changeRate,
    };
  }

  return result;
}

// ========================================
// FXレート計算（変動率比較方式）
// ========================================

/**
 * FXレートを計算（変動率比較方式）
 * レート = base属性の指数 / quote属性の指数
 *
 * @param {string} baseAttribute - 基軸属性
 * @param {string} quoteAttribute - 決済属性
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @returns {number} FXレート
 */
export function calculateFXRate(baseAttribute, quoteAttribute, marketIndex, marketState) {
  const baseIndex = getAttributeIndex(baseAttribute, marketIndex, marketState);
  const quoteIndex = getAttributeIndex(quoteAttribute, marketIndex, marketState);

  if (quoteIndex === 0) {
    return 1; // ゼロ除算防止
  }

  return baseIndex / quoteIndex;
}

/**
 * FXレートの変動率を計算
 * 基準レート（両方100の時）は1.0なので、そこからの乖離を計算
 *
 * @param {string} baseAttribute - 基軸属性
 * @param {string} quoteAttribute - 決済属性
 * @param {Object} marketIndex - マーケットインデックス
 * @param {Object} marketState - 市場状態
 * @returns {number} 変動率（%）
 */
export function getFXChangeRate(baseAttribute, quoteAttribute, marketIndex, marketState) {
  const rate = calculateFXRate(baseAttribute, quoteAttribute, marketIndex, marketState);
  // 基準レート1.0からの変動率（%）
  return (rate - 1) * 100;
}

// ========================================
// エクスポート
// ========================================

export default {
  clearCacheIfNeeded,
  clearCache,
  calculateCardsIndex,
  getAttributeIndex,
  getCategoryIndex,
  getCategoriesIndex,
  getLowCostIndex,
  getHighCostIndex,
  getMarketIndex,
  getFundIndex,
  getFundPrice,
  getFundChangeRate,
  getAllFundIndices,
  calculateFXRate,
  getFXChangeRate,
};
